/**
 * DialogContent Component
 *
 * Provides the scrollable content area for dialogs with dynamic theme integration.
 * Supports scrolling, dividers, and various padding configurations.
 *
 * IMPORTANT: ALL styles use theme tokens - NO hardcoded values.
 *
 * Basic Usage:
 * - Wrap your dialog content with DialogContent
 * - Use density prop for different spacing (comfortable, standard, compact)
 * - Use variant prop for padding control (default, noPadding, compact)
 *
 * Scrolling Configuration:
 * - Auto-scrolling activates when content exceeds maxHeight
 * - Use disableScrolling to clip content instead of scrolling
 * - Supports both string and numeric maxHeight values
 *
 * Visual Dividers:
 * - Use dividerTop and dividerBottom for visual separation
 * - Dividers extend edge-to-edge when variant is noPadding
 *
 * Integration:
 * - Works seamlessly with EmbeddedDialog, DialogHeader, and DialogFooter
 * - Automatically handles theme switching and accessibility
 */

import React from 'react';
import {
  Box,
  Divider,
  useTheme
} from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';

// ============================================================================
// TYPES
// ============================================================================

export type DialogContentVariant = 'default' | 'noPadding' | 'compact';
export type DialogContentDensity = 'comfortable' | 'standard' | 'compact';

export interface DialogContentProps {
  /**
   * Content to display
   */
  children: React.ReactNode;

  /**
   * Variant controls overall styling approach
   * @default 'default'
   */
  variant?: DialogContentVariant;

  /**
   * Density controls spacing
   * @default 'standard'
   */
  density?: DialogContentDensity;

  /**
   * Show divider at top
   * @default false
   */
  dividerTop?: boolean;

  /**
   * Show divider at bottom
   * @default false
   */
  dividerBottom?: boolean;

  /**
   * Override scrolling behavior
   * @default false
   */
  disableScrolling?: boolean;

  /**
   * Maximum height before scrolling activates
   */
  maxHeight?: string | number;

  /**
   * Custom styles using theme tokens
   */
  sx?: SxProps<Theme>;

  /**
   * Class name for styling
   */
  className?: string;

  /**
   * Test ID for testing
   */
  'data-testid'?: string;

  /**
   * ARIA label for accessibility
   */
  'aria-label'?: string;

  /**
   * ARIA labelled by for accessibility
   */
  'aria-labelledby'?: string;

  /**
   * ARIA described by for accessibility
   */
  'aria-describedby'?: string;
}

// ============================================================================
// COMPONENT
// ============================================================================

export const DialogContent = React.memo<DialogContentProps>(({
  children,
  variant = 'default',
  density = 'standard',
  dividerTop = false,
  dividerBottom = false,
  disableScrolling = false,
  maxHeight,
  sx,
  className,
  'data-testid': dataTestId = 'dialog-content',
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
}) => {
  const theme = useTheme();

  // Calculate padding based on variant and density
  const getPadding = () => {
    if (variant === 'noPadding') return 0;

    switch (density) {
      case 'comfortable':
        return theme.spacing(3);
      case 'compact':
        return theme.spacing(1);
      case 'standard':
      default:
        return theme.spacing(2);
    }
  };

  // Calculate max height with theme spacing if not provided
  const getMaxHeight = () => {
    if (maxHeight) return maxHeight;

    // Default based on variant
    switch (variant) {
      case 'compact':
        return '400px';
      default:
        return '500px';
    }
  };

  // Base styles using theme tokens ONLY
  const baseStyles: SxProps<Theme> = {
    padding: getPadding(),
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,

    // Scrolling configuration
    ...(!disableScrolling && {
      overflow: 'auto',
      maxHeight: getMaxHeight(),

      // Custom scrollbar styling with theme colors
      '&::-webkit-scrollbar': {
        width: theme.spacing(1),
      },
      '&::-webkit-scrollbar-track': {
        backgroundColor: theme.palette.action.hover,
        borderRadius: theme.shape.borderRadius,
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: theme.palette.action.disabled,
        borderRadius: theme.shape.borderRadius,
        '&:hover': {
          backgroundColor: theme.palette.action.focus,
        },
      },

      // Firefox scrollbar styling
      scrollbarWidth: 'thin',
      scrollbarColor: `${theme.palette.action.disabled} ${theme.palette.action.hover}`,
    }),

    // Remove focus outline for content clicks
    '&:focus': {
      outline: 'none',
    },

    // Ensure proper text rendering
    lineHeight: theme.typography.body1.lineHeight,
    fontSize: theme.typography.body1.fontSize,
    fontFamily: theme.typography.body1.fontFamily,
  };

  return (
    <Box
      className={className}
      data-testid={dataTestId}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      aria-describedby={ariaDescribedBy}
      role="region"
      tabIndex={-1}
      sx={[
        baseStyles,
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {/* Top divider */}
      {dividerTop && (
        <Divider
          sx={{
            marginBottom: getPadding(),
            marginLeft: variant === 'noPadding' ? 0 : `-${getPadding()}`,
            marginRight: variant === 'noPadding' ? 0 : `-${getPadding()}`,
            borderColor: theme.palette.divider,
          }}
          data-testid={`${dataTestId}-divider-top`}
        />
      )}

      {/* Content */}
      <Box
        data-testid={`${dataTestId}-body`}
        sx={{
          // Ensure content takes full width
          width: '100%',

          // Apply theme typography to nested elements
          '& > *:first-of-type': {
            marginTop: 0,
          },
          '& > *:last-child': {
            marginBottom: 0,
          },
        }}
      >
        {children}
      </Box>

      {/* Bottom divider */}
      {dividerBottom && (
        <Divider
          sx={{
            marginTop: getPadding(),
            marginLeft: variant === 'noPadding' ? 0 : `-${getPadding()}`,
            marginRight: variant === 'noPadding' ? 0 : `-${getPadding()}`,
            borderColor: theme.palette.divider,
          }}
          data-testid={`${dataTestId}-divider-bottom`}
        />
      )}
    </Box>
  );
});

// Set display name for debugging
DialogContent.displayName = 'DialogContent';

export default DialogContent;